import { Params, QueryParamsHandling } from '@angular/router';
import { getUrl, RouteDef } from '@cumlaude/shared-components-menu';
import { IconName } from '@cumlaude/shared-components-icons';
import { isTopLevelRoute, routeNaam, UserRouteData } from '../../services/user-routes.service';

/**
 * Eén navigeerbare node in de verticale hoofdnavigatie.
 */
export interface NavigatieItem {
	readonly url: string;
	readonly titel: string;
	readonly icon?: IconName;
	readonly topLevel: boolean;
	readonly firstLeaf: RouteDef<UserRouteData>;
	readonly children: readonly NavigatieItem[];
}

/**
 * Eén zichtbare regel in de rail: een NavigatieItem plus context (inspringniveau, open/actief-state, queryparams).
 */
export interface NavigatieRij {
	readonly item: NavigatieItem;
	readonly niveau: number;
	readonly hasChildren: boolean;
	readonly uitgeklapt: boolean;
	readonly firstChild: boolean;
	readonly ancestorLijnen: readonly boolean[];
	readonly actief: boolean;
	readonly bevatActief: boolean;
	readonly queryParams?: Params;
	readonly queryParamsHandling?: QueryParamsHandling;
}

/**
 * Bouwt een NavigatieItem uit een RouteDef. Een node met `eigenTabbalk` wordt behandeld als leaf
 * (geen chevron/uitklap) ook al heeft de route zelf children -- die horen dan bij de eigen tabbalk
 * (bv. Details > Leerling). `firstLeaf` wordt wel altijd via de echte children afgeleid, anders
 * landt de queryparam-matching op de groep-route, die geen `fixedWeergaveOpties` heeft.
 */
export function bouwNavigatieItem(route: RouteDef<UserRouteData>): NavigatieItem {
	const routeData = route.routeData!;
	const allChildren = route.subRoutes?.map((subRoute) => bouwNavigatieItem(subRoute)) ?? [];

	return {
		url: getUrl({ routeElements: route.elements! }),
		titel: routeNaam(routeData),
		icon: routeData.icon,
		topLevel: isTopLevelRoute(routeData),
		firstLeaf: allChildren.length > 0 ? allChildren[0].firstLeaf : route,
		children: routeData.eigenTabbalk ? [] : allChildren,
	};
}

/**
 * Geeft de urls van alle ancestor-groepen van `pad`, van het hoogste niveau naar het diepste
 * (exclusief `pad` zelf). Gebruikt om bij een deeplink/refresh de tak naar de actieve pagina
 * automatisch open te zetten.
 */
export function ancestorUrls(pad: string | undefined): string[] {
	if (!pad) return [];
	const segmenten = pad.split('/').filter(Boolean);
	const ancestors: string[] = [];
	for (let i = 1; i < segmenten.length; i++) {
		ancestors.push('/' + segmenten.slice(0, i).join('/'));
	}
	return ancestors;
}

/**
 * Bepaalt de actief-status van `item`: of het zelf de actieve rij is (`actief`) en of het de
 * actieve pagina als (klein)kind bevat (`bevatActief`, gebruikt om een dichte sectie die de actieve
 * pagina bevat toch te highlighten in de ingeklapte rail -- zie NavigatieRijComponent.toontAlsActief).
 *
 * `actief` is normaal een exacte match tussen `huidigPad` en `item.url`. Een blad zonder children
 * in de rail (`hasChildren` false, bv. door `eigenTabbalk` -- zie `bouwNavigatieItem`) heeft echter
 * geen eigen rij voor zijn subpagina's; daarvoor telt `bevatActief` ook mee als "actief", anders zou
 * zo'n item nooit als actief getoond worden terwijl je er middenin zit.
 */
function actiefStatus(item: NavigatieItem, hasChildren: boolean, huidigPad: string | undefined): { actief: boolean; bevatActief: boolean } {
	const bevatActief = !!huidigPad && huidigPad.startsWith(item.url + '/');
	const actief = huidigPad === item.url || (!hasChildren && bevatActief);
	return { actief, bevatActief };
}

/**
 * Slaat de boom plat tot de zichtbare rijen: een hoofdpagina/groep gevolgd door zijn children als
 * en alleen als hij openstaat.
 */
export function platteRijen(
	items: readonly NavigatieItem[],
	open: ReadonlySet<string>,
	huidigPad: string | undefined,
	queryParamsVoorItem: (item: NavigatieItem) => { queryParams?: Params; queryParamsHandling?: QueryParamsHandling },
	niveau = 0,
	ancestorLijnen: readonly boolean[] = []
): NavigatieRij[] {
	const rijen: NavigatieRij[] = [];
	items.forEach((item, index) => {
		const hasChildren = item.children.length > 0;
		const uitgeklapt = hasChildren && open.has(item.url);
		const { actief, bevatActief } = actiefStatus(item, hasChildren, huidigPad);
		const firstChild = niveau > 0 && index === 0;
		const hasNext = index < items.length - 1;
		const { queryParams, queryParamsHandling } = queryParamsVoorItem(item);

		rijen.push({ item, niveau, hasChildren, uitgeklapt, actief, bevatActief, firstChild, ancestorLijnen, queryParams, queryParamsHandling });

		if (uitgeklapt) {
			const childAncestorLijnen = niveau === 0 ? ancestorLijnen : [...ancestorLijnen, hasNext];
			rijen.push(...platteRijen(item.children, open, huidigPad, queryParamsVoorItem, niveau + 1, childAncestorLijnen));
		}
	});
	return rijen;
}
